import { useModules } from '../lib/modules-context';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, HardHat, Zap, ArrowRight } from 'lucide-react';

const NUDGES = {
  // Financial → Market nudges
  'financial:no_market_validation': {
    module: 'market',
    text: 'Tu análisis puede mejorar con datos de mercado',
    sub: 'Valida precio y absorción con comparables reales.',
    icon: TrendingUp,
    color: 'var(--market-primary)',
    bg: 'var(--market-light)',
    cta: 'Explorar Market Intelligence',
    path: '/market',
  },
  'financial:unvalidated_assumptions': {
    module: 'market',
    text: 'Este escenario depende de supuestos no validados',
    sub: 'Agrega comparables para respaldar tu precio y absorción.',
    icon: TrendingUp,
    color: 'var(--market-primary)',
    bg: 'var(--market-light)',
    cta: 'Validar supuestos',
    path: '/market',
  },
  'financial:no_construction_tracking': {
    module: 'construction',
    text: 'Monitorea la ejecución real de tu proyecto',
    sub: 'Detecta desviaciones de costo y tiempo antes de que impacten la rentabilidad.',
    icon: HardHat,
    color: 'var(--construction-primary)',
    bg: 'var(--construction-light)',
    cta: 'Ver Construction Intelligence',
    path: '/construction',
  },
  // Market → upgrade nudges
  'market:price_change_impact': {
    module: 'financial',
    text: 'Este cambio puede impactar tu rentabilidad',
    sub: 'Ejecuta el análisis financiero para ver el efecto en TIR y VAN.',
    icon: Zap,
    color: 'var(--financial-primary)',
    bg: 'var(--financial-light)',
    cta: 'Ver impacto financiero',
    path: '/analyzer',
    alwaysShow: true,
  },
  // Dashboard nudges
  'dashboard:complete_analysis': {
    module: 'market',
    text: 'Completa tu análisis con inteligencia de mercado',
    sub: 'Los proyectos con validación de mercado tienen supuestos más confiables.',
    icon: TrendingUp,
    color: 'var(--market-primary)',
    bg: 'var(--market-light)',
    cta: 'Agregar comparables',
    path: '/market',
  },
};

/**
 * ContextualNudge — Shows a conversion message if the target module is inactive.
 * If the module is active, shows nothing (zero noise).
 * Usage: <ContextualNudge nudgeKey="financial:no_market_validation" />
 */
export function ContextualNudge({ nudgeKey }) {
  const { isActive } = useModules();
  const navigate = useNavigate();
  const nudge = NUDGES[nudgeKey];
  if (!nudge) return null;

  // Don't show if the target module is already active (unless alwaysShow)
  if (!nudge.alwaysShow && isActive(nudge.module)) return null;

  const Icon = nudge.icon;

  return (
    <div
      className="rounded-lg px-3.5 py-2.5 flex items-center gap-3"
      style={{ background: nudge.bg, border: `1px solid ${nudge.color}12` }}
      data-testid={`nudge-${nudgeKey.replace(/[:.]/g, '-')}`}
    >
      <Icon className="w-4 h-4 shrink-0" style={{ color: nudge.color }} />
      <div className="flex-1 min-w-0">
        <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{nudge.text}</span>
        <span className="text-[10px] ml-1" style={{ color: 'var(--text-secondary)' }}>{nudge.sub}</span>
      </div>
      <button
        onClick={() => navigate(nudge.path)}
        className="shrink-0 text-[10px] font-semibold whitespace-nowrap flex items-center gap-1 transition-opacity hover:opacity-80"
        style={{ color: nudge.color }}
      >
        {nudge.cta} <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}
